import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { MP_SURF } from '../constants/surfaces.js';
import { mpPaginationBtnStyle, mpPaginationPageBtnStyle } from '../constants/paginationStyles.js';
import { CONDITIONS_INFO } from '../../constants/musicConstants.js';
import { GLOBAL_CSS } from '../../constants/musicStyles.js';
import { MARKET_SORT_OPTIONS } from '../marketplaceConstants.js';
import { MarketplaceResultsToolbar } from '../components/MarketplaceControls.js';
import MarketplaceFiltersDrawerPanel from '../components/MarketplaceFiltersDrawerPanel.jsx';
import { GridItem, ListaItem } from '../components/MarketplaceItemCards.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import { DCard, BtnGold, BtnGhost, FieldInput } from '../components/MarketplaceAtoms.js';
import AdSlot from '../components/AdSlot.jsx';
import {
  submitAuctionForVerification,
  uploadAuctionListingPhotos,
} from '../../lib/auctionsService.js';
import { fetchPartnersForListingCoverage, formatPartnerPlaceLabel } from '../../lib/listingPartnerLogisticsService.js';
import { useFiltersDrawerFab } from '../context/FiltersDrawerFabContext.js';

function CheckboxRow({ checked, onToggle, title, desc }) {
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(); } }}
      onClick={onToggle}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 10, border: `0.5px solid ${checked ? C.gold : 'rgba(255,255,255,0.1)'}`, background: checked ? 'rgba(200,168,75,0.06)' : 'rgba(255,255,255,0.03)', marginBottom: 8, cursor: 'pointer' }}
    >
      <div style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${checked ? C.gold : 'rgba(255,255,255,0.2)'}`, background: checked ? C.gold : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
        {checked && <svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="#000" strokeWidth="2.5" fill="none" /></svg>}
      </div>
      <div>
        <div style={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>{title}</div>
        {desc && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2, lineHeight: 1.35 }}>{desc}</div>}
      </div>
    </div>
  );
}

/** Altura barra principal (WebTopBar sin `mini`, `barMinHeight` 80). */
const SUBASTAS_NAV_TOP_PX = 80;

export default function PageSubastas({
  onBack,
  onNavigate,
  user,
  onRequireAuth,
  totalFiltered = 0,
  auctionPageItems = [],
  totalPages = 1,
  currentPage = 1,
  setCurrentPage,
  vista = 4,
  setVista,
  gridCols,
  generosSel = [],
  removeGenero,
  ordenar,
  setOrdenar,
  likes = {},
  toggleLike,
  setModalTab,
  setShowModal,
  onClearFilters,
  filters,
  filterActions,
  tooltipCond,
  setTooltipCond,
  filterCount = 0,
  openNuevaSubastaRequest = false,
  onConsumedOpenNuevaSubasta,
}) {
  const consumedNuevaSubastaRef = useRef(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const { setDrawerOpen: setFiltersDrawerFabOpen } = useFiltersDrawerFab();

  useEffect(() => {
    setFiltersDrawerFabOpen(filtersDrawerOpen);
    return () => setFiltersDrawerFabOpen(false);
  }, [filtersDrawerOpen, setFiltersDrawerFabOpen]);

  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [aTitulo, setATitulo] = useState('');
  const [aArtista, setAArtista] = useState('');
  const [aPujaMin, setAPujaMin] = useState('');
  const [aReserva, setAReserva] = useState('');
  const [aDuracion, setADuracion] = useState('48');
  const [aFormato, setAFormato] = useState('VINILO');
  const [aEstado, setAEstado] = useState('');
  const [aNotas, setANotas] = useState('');
  const [cmpNormas, setCmpNormas] = useState(false);
  const [cmpTitular, setCmpTitular] = useState(false);
  const [cmpGarantia, setCmpGarantia] = useState(false);
  const [cmpDatos, setCmpDatos] = useState(false);
  const [auctionFormErr, setAuctionFormErr] = useState('');
  const [auctionSent, setAuctionSent] = useState(false);
  const [auctionSubmitBusy, setAuctionSubmitBusy] = useState(false);
  const [aPartnerId, setAPartnerId] = useState('');
  const [verifierPartners, setVerifierPartners] = useState([]);
  const [verifierPartnersLoading, setVerifierPartnersLoading] = useState(false);
  const [auctionSuccessPartner, setAuctionSuccessPartner] = useState('');
  const fotosInputRefs = [useRef(null), useRef(null), useRef(null)];
  const [fotosFiles, setFotosFiles] = useState([null, null, null]);
  const [fotosPreviews, setFotosPreviews] = useState([null, null, null]);

  const closeAuctionModal = () => {
    setShowAuctionModal(false);
    setAuctionFormErr('');
    setAuctionSent(false);
    setAuctionSuccessPartner('');
    setAPartnerId('');
    setFotosFiles([null, null, null]);
    setFotosPreviews([null, null, null]);
    setATitulo('');
    setAArtista('');
    setAPujaMin('');
    setAReserva('');
    setADuracion('48');
    setAFormato('VINILO');
    setAEstado('');
    setANotas('');
    setCmpNormas(false);
    setCmpTitular(false);
    setCmpGarantia(false);
    setCmpDatos(false);
  };

  const openCrearSubasta = useCallback(() => {
    if (!user) {
      onRequireAuth?.();
      return;
    }
    setAuctionFormErr('');
    setAuctionSent(false);
    setAuctionSuccessPartner('');
    setShowAuctionModal(true);
    setVerifierPartnersLoading(true);
    fetchPartnersForListingCoverage('store', 'physical').then(({ partners, error }) => {
      setVerifierPartnersLoading(false);
      if (error) {
        setAuctionFormErr('No se pudieron cargar las tiendas verificadoras.');
        setVerifierPartners([]);
        return;
      }
      setVerifierPartners(partners);
      if (partners.length === 1) setAPartnerId(partners[0].partner_id);
    });
  }, [user, onRequireAuth]);

  const handleFotoSelect = (e, index) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotosFiles(prev => {
      const next = [...prev];
      next[index] = file;
      return next;
    });
    const url = URL.createObjectURL(file);
    setFotosPreviews(prev => {
      const next = [...prev];
      if (next[index]) URL.revokeObjectURL(next[index]);
      next[index] = url;
      return next;
    });
    e.target.value = '';
  };

  useEffect(() => {
    if (!openNuevaSubastaRequest) {
      consumedNuevaSubastaRef.current = false;
      return;
    }
    if (consumedNuevaSubastaRef.current) return;
    consumedNuevaSubastaRef.current = true;
    openCrearSubasta();
    onConsumedOpenNuevaSubasta?.();
  }, [openNuevaSubastaRequest, openCrearSubasta, onConsumedOpenNuevaSubasta]);

  const submitAuction = async () => {
    setAuctionFormErr('');
    if (!aTitulo.trim() || !aArtista.trim()) {
      setAuctionFormErr('Completa título y artista.');
      return;
    }
    if (!aPartnerId) {
      setAuctionFormErr('Elige la tienda verificadora donde llevarás el disco.');
      return;
    }
    if (!fotosFiles[0]) {
      setAuctionFormErr('Añade al menos la foto de portada.');
      return;
    }
    if (!aPujaMin || Number(aPujaMin) <= 0) {
      setAuctionFormErr('Indica una puja inicial válida (€).');
      return;
    }
    if (!aEstado) {
      setAuctionFormErr('Selecciona el estado del disco.');
      return;
    }
    if (!cmpNormas || !cmpTitular || !cmpGarantia || !cmpDatos) {
      setAuctionFormErr('Debes aceptar todas las declaraciones de cumplimiento para continuar.');
      return;
    }
    setAuctionSubmitBusy(true);
    const hours = Number(aDuracion) || 48;
    const partnerLabel =
      formatPartnerPlaceLabel(verifierPartners.find(p => p.partner_id === aPartnerId)) || 'la tienda elegida';

    const { id, error } = await submitAuctionForVerification({
      partnerId: aPartnerId,
      title: aTitulo.trim(),
      artist: aArtista.trim(),
      format: aFormato,
      condition: aEstado,
      auctionStart: Number(aPujaMin),
      durationHours: hours,
      auctionBuyNow: aReserva ? Number(aReserva) : null,
      description: aNotas.trim() || null,
    });

    if (error || !id) {
      setAuctionSubmitBusy(false);
      setAuctionFormErr(error || 'No se pudo registrar la solicitud.');
      return;
    }

    const { error: photoErr } = await uploadAuctionListingPhotos(id, user.id, fotosFiles);
    setAuctionSubmitBusy(false);
    if (photoErr) {
      setAuctionFormErr(`Solicitud creada, pero falló la subida de fotos: ${photoErr}`);
      return;
    }

    setAuctionSuccessPartner(partnerLabel);
    setAuctionSent(true);
  };

  const goFicha = item => {
    if (!user) {
      setModalTab?.('login');
      setShowModal?.(true);
      return;
    }
    onNavigate?.('ficha', item);
  };

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <style>{GLOBAL_CSS}</style>
      <WebTopBar
        navActive="subastas"
        dark
        user={user}
        onNavigate={onNavigate}
        onShowModal={tab => {
          setModalTab(typeof tab === 'string' ? tab : 'login');
          setShowModal(true);
        }}
        onSignOut={onBack}
      />

      <MarketplaceFiltersDrawerPanel
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        onClearFilters={onClearFilters ?? filterActions?.limpiar}
        topPx={SUBASTAS_NAV_TOP_PX}
        filters={filters}
        actions={filterActions}
        tooltipCond={tooltipCond}
        setTooltipCond={setTooltipCond}
      />

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 500, color: '#fff', marginBottom: 4 }}>Subastas en vivo</h1>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Discos verificados · Pujas anónimas · Extensión automática 5min</p>
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            <strong style={{ color: C.gold }}>{totalFiltered}</strong> con filtros actuales
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 0, padding: '14px 18px', borderRadius: 12, background: 'rgba(200,168,75,0.07)', border: '0.5px solid rgba(200,168,75,0.28)' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>¿Tienes un disco para subastar?</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>
              Solicita una subasta: elige tienda verificadora, lleva el disco y la subasta se publicará al verificarlo.
            </div>
          </div>
          <button type="button" onClick={openCrearSubasta}
            style={{ background: C.gold, color: '#000', border: 'none', padding: '10px 20px', borderRadius: 10, fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer', letterSpacing: '0.04em', boxShadow: '0 2px 10px rgba(200,168,75,0.28)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            + CREAR SUBASTA
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 20px 48px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 200px', gap: 18, alignItems: 'start' }}>
            <div style={{ minWidth: 0 }}>
              <MarketplaceResultsToolbar
                total={totalFiltered}
                totalPages={totalPages}
                currentPage={currentPage}
                generosSel={generosSel}
                onRemoveGenero={removeGenero}
                vista={vista}
                onVistaChange={setVista}
                ordenar={ordenar}
                onOrdenarChange={setOrdenar}
                sortOptions={MARKET_SORT_OPTIONS}
                goldColor={C.gold}
                onToggleFilters={() => setFiltersDrawerOpen(o => !o)}
                filtersOpen={filtersDrawerOpen}
                filterCount={filterCount}
                onFeedNovedades={() => {
                  setOrdenar?.('recientes');
                  setCurrentPage?.(1);
                }}
                onFeedRecomendaciones={() => {
                  setOrdenar?.('recomendaciones');
                  setCurrentPage?.(1);
                }}
              />

              {totalFiltered === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: MP_SURF.textMuted }}>
                  <div style={{ fontSize: '2.4rem', marginBottom: 9 }}>🔍</div>
                  <p style={{ color: MP_SURF.textMuted, maxWidth: 400, margin: '0 auto', lineHeight: 1.55 }}>No hay subastas con estos criterios (o ninguna en catálogo).</p>
                  <button type="button" onClick={() => onClearFilters?.()} style={{ marginTop: 13, background: C.gold, color: '#000', border: 'none', padding: '8px 20px', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}>Limpiar filtros</button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: vista === 1 ? 0 : 14 }}>
                    {auctionPageItems.map(item => (vista === 1
                      ? <ListaItem key={item.id} item={item} liked={likes[item.id]} onLike={() => toggleLike?.(item.id)} onClick={() => goFicha(item)} />
                      : <GridItem key={item.id} item={item} liked={likes[item.id]} onLike={() => toggleLike?.(item.id)} onClick={() => goFicha(item)} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 24, paddingBottom: 8 }}>
                      <button type="button" onClick={() => setCurrentPage?.(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                        style={mpPaginationBtnStyle({ active: false, disabled: currentPage === 1 })}>
                        ←
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button key={p} type="button" onClick={() => setCurrentPage?.(p)}
                          style={mpPaginationPageBtnStyle(p === currentPage)}>
                          {p}
                        </button>
                      ))}
                      <button type="button" onClick={() => setCurrentPage?.(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                        style={mpPaginationBtnStyle({ active: false, disabled: currentPage === totalPages })}>
                        →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ position: 'sticky', top: SUBASTAS_NAV_TOP_PX + 6, display: 'flex', flexDirection: 'column', alignSelf: 'start' }}>
              <div
                style={{
                  width: 200,
                  height: 600,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 7,
                  padding: 0,
                  margin: 0,
                  overflow: 'hidden',
                }}
              >
                <AdSlot slot="lateral_mercado_subastas" />
              </div>
            </div>
          </div>
      </div>

      {showAuctionModal && (
        <>
          <div onClick={() => { if (!auctionSent) closeAuctionModal(); }} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:400, animation:'fadeIn .2s' }} />
          <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', zIndex:401, width:'min(720px,94vw)', maxHeight:'min(640px,92vh)', overflowY:'auto', borderRadius:14, boxShadow:'0 24px 60px rgba(0,0,0,0.45)', animation:'slideInDown .22s ease' }}>
            <DCard style={{ padding:22, borderColor:'rgba(200,168,75,0.2)' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:16 }}>
                <div>
                  <h2 style={{ fontSize:17, fontWeight:600, color:'#fff', margin:0 }}>Nueva subasta</h2>
                  <p style={{ fontSize:11, color:'rgba(255,255,255,0.45)', margin:'6px 0 0', lineHeight:1.45 }}>Elige tienda verificadora, sube fotos y condiciones de puja. La subasta no será pública hasta que el partner verifique el disco físicamente.</p>
                </div>
                <button type="button" onClick={() => { if (!auctionSent) closeAuctionModal(); }} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.35)', fontSize:20, cursor:'pointer', lineHeight:1, padding:4 }} aria-label="Cerrar">✕</button>
              </div>

              {!auctionSent ? (
                <>
                  <DCard style={{ padding:14, marginBottom:16, background:'rgba(200,168,75,0.08)', borderColor:'rgba(200,168,75,0.28)' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:C.gold, marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                      <Icon name="info" size={14} color={C.gold} /> Antes de publicar la subasta
                    </div>
                    {['Debes llevar el disco a la tienda verificadora elegida','La subasta se publica automáticamente cuando el partner marca «Verificado»','Las fotos deben mostrar el estado real del soporte y la funda','La puja inicial y la reserva son vinculantes según las normas del marketplace'].map((s, i) => (
                      <div key={s} style={{ display:'flex', gap:8, marginBottom:4, alignItems:'flex-start' }}>
                        <div style={{ width:16, height:16, borderRadius:'50%', background:'rgba(200,168,75,0.2)', color:C.gold, fontSize:9, fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>{i + 1}</div>
                        <span style={{ fontSize:11, color:'rgba(255,255,255,0.58)', lineHeight:1.4 }}>{s}</span>
                      </div>
                    ))}
                  </DCard>

                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 5 }}>Tienda verificadora *</div>
                    {verifierPartnersLoading ? (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Cargando tiendas…</div>
                    ) : verifierPartners.length === 0 ? (
                      <div style={{ fontSize: 12, color: C.danger }}>No hay tiendas verificadoras disponibles.</div>
                    ) : (
                      <select
                        value={aPartnerId}
                        onChange={e => setAPartnerId(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '0.5px solid rgba(255,255,255,0.12)',
                          background: 'rgba(255,255,255,0.06)',
                          color: '#fff',
                          fontSize: 13,
                          outline: 'none',
                          fontFamily: 'inherit',
                        }}>
                        <option value="">Selecciona dónde llevarás el disco…</option>
                        {verifierPartners.map(p => (
                          <option key={p.partner_id} value={p.partner_id}>
                            {formatPartnerPlaceLabel(p)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Fotos · mínimo portada *</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[
                        { label: '★ Portada', index: 0 },
                        { label: 'Cara disco', index: 1 },
                        { label: 'Otros', index: 2 },
                      ].map(({ label, index }) => (
                        <div key={index} style={{ flex: 1, minWidth: 0 }}>
                          <input
                            ref={fotosInputRefs[index]}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={e => handleFotoSelect(e, index)}
                          />
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => fotosInputRefs[index].current?.click()}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') fotosInputRefs[index].current?.click();
                            }}
                            style={{
                              width: '100%',
                              aspectRatio: '1',
                              borderRadius: 8,
                              border: fotosPreviews[index]
                                ? `1.5px solid ${index === 0 ? C.gold : 'rgba(255,255,255,0.2)'}`
                                : index === 0
                                  ? '1.5px dashed rgba(200,168,75,0.4)'
                                  : '1.5px dashed rgba(255,255,255,0.15)',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              background: fotosPreviews[index] ? 'transparent' : 'rgba(255,255,255,0.03)',
                            }}>
                            {fotosPreviews[index] ? (
                              <img src={fotosPreviews[index]} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.25)' }}>+</span>
                            )}
                          </div>
                          <div style={{ fontSize: 9, color: index === 0 ? C.gold : 'rgba(255,255,255,0.35)', textAlign: 'center', marginTop: 4 }}>
                            {label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <FieldInput label="Artista *" value={aArtista} onChange={e => setAArtista(e.target.value)} placeholder="ej. Pink Floyd" />
                  <FieldInput label="Título del disco *" value={aTitulo} onChange={e => setATitulo(e.target.value)} placeholder="ej. The Dark Side of the Moon" />

                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginBottom:5 }}>Formato *</div>
                    <select value={aFormato} onChange={e => setAFormato(e.target.value)} style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'0.5px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.06)', color:'#fff', fontSize:13, outline:'none', fontFamily:'inherit' }}
                      onFocus={e => { e.target.style.borderColor = C.gold; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
                      <option value="VINILO">Vinilo 12&quot;</option>
                      <option value="VINILO_7">Vinilo 7&quot;</option>
                      <option value="CD">CD</option>
                      <option value="CASETE">Casete</option>
                      <option value="BLURAY">Blu-ray Audio</option>
                    </select>
                  </div>

                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginBottom:5 }}>Estado del artículo *</div>
                    <select value={aEstado} onChange={e => setAEstado(e.target.value)} style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'0.5px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.06)', color:'#fff', fontSize:13, outline:'none', fontFamily:'inherit' }}
                      onFocus={e => { e.target.style.borderColor = C.gold; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
                      <option value="">Selecciona…</option>
                      {CONDITIONS_INFO.map(c => (
                        <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:4 }}>
                    <FieldInput label="Puja inicial (€) *" type="number" min="1" value={aPujaMin} onChange={e => setAPujaMin(e.target.value)} placeholder="ej. 15" />
                    <FieldInput label="Precio reserva (€, opcional)" type="number" min="0" value={aReserva} onChange={e => setAReserva(e.target.value)} placeholder="Mínimo para vender" />
                  </div>

                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginBottom:5 }}>Duración de la subasta *</div>
                    <select value={aDuracion} onChange={e => setADuracion(e.target.value)} style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'0.5px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.06)', color:'#fff', fontSize:13, outline:'none', fontFamily:'inherit' }}
                      onFocus={e => { e.target.style.borderColor = C.gold; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
                      <option value="24">24 horas</option>
                      <option value="48">48 horas</option>
                      <option value="72">72 horas</option>
                      <option value="168">7 días</option>
                    </select>
                  </div>

                  <div style={{ marginBottom:14 }}>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginBottom:5 }}>Descripción y notas para compradores</div>
                    <textarea rows={3} value={aNotas} onChange={e => setANotas(e.target.value)} placeholder="Edición, defectos visibles, incluye insertos…" style={{ width:'100%', padding:'10px 12px', borderRadius:10, border:'0.5px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.06)', color:'#fff', fontSize:13, outline:'none', fontFamily:'inherit', resize:'vertical' }}
                      onFocus={e => { e.target.style.borderColor = C.gold; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; }} />
                  </div>

                  <div style={{ fontSize:11, fontWeight:600, color:C.gold, marginBottom:8, letterSpacing:'0.04em' }}>CUMPLIMIENTO Y DECLARACIONES *</div>
                  <CheckboxRow checked={cmpNormas} onToggle={() => setCmpNormas(v => !v)} title="Normas de subasta y comisiones" desc="He leído y acepto las normas de subastas DreamVinyl, incluida la comisión sobre el precio final y las extensiones de tiempo en caso de pujas en el último minuto." />
                  <CheckboxRow checked={cmpTitular} onToggle={() => setCmpTitular(v => !v)} title="Titularidad y veracidad" desc="Declaro que soy titular del artículo, que existe físicamente y que las fotos y la descripción reflejan fielmente su estado." />
                  <CheckboxRow checked={cmpGarantia} onToggle={() => setCmpGarantia(v => !v)} title="Pujas y garantías" desc="Acepto que las pujas pueden estar sujetas a depósito de garantía o validación de medio de pago, y que las pujas ganadoras son vinculantes salvo incumplimiento acreditado." />
                  <CheckboxRow checked={cmpDatos} onToggle={() => setCmpDatos(v => !v)} title="Tratamiento de datos (RGPD)" desc="Autorizo el tratamiento de mis datos para gestionar esta subasta, notificaciones relacionadas y prevención de fraude, según la Política de privacidad." />

                  {auctionFormErr && (
                    <div style={{ marginTop:10, marginBottom:4, background:'rgba(226,75,74,0.1)', border:'0.5px solid rgba(226,75,74,0.25)', borderRadius:8, padding:'9px 11px', color:C.danger, fontSize:12 }}>{auctionFormErr}</div>
                  )}

                  <BtnGold onClick={submitAuction} disabled={auctionSubmitBusy || verifierPartnersLoading} style={{ marginTop:14, borderRadius:14, padding:14, fontSize:15, fontWeight:700 }}>{auctionSubmitBusy ? 'Enviando…' : 'Enviar solicitud de subasta'}</BtnGold>
                  <BtnGhost onClick={closeAuctionModal} style={{ marginTop:8 }}>Cancelar</BtnGhost>
                </>
              ) : (
                <div style={{ textAlign:'center', padding:'20px 8px 8px' }}>
                  <div style={{ fontSize:40, marginBottom:10 }}>✓</div>
                  <div style={{ fontSize:16, fontWeight:600, color:'#fff', marginBottom:6 }}>Solicitud registrada</div>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.5)', lineHeight:1.55, marginBottom:18 }}>
                    Lleva el disco a <strong style={{ color: '#fff' }}>{auctionSuccessPartner || 'la tienda verificadora'}</strong>.
                    La subasta se publicará automáticamente cuando la tienda complete la verificación (estado «Verificado» en custodia).
                    Te avisaremos en la app.
                  </p>
                  <BtnGold onClick={closeAuctionModal} style={{ borderRadius:14, padding:12, fontSize:14 }}>Entendido</BtnGold>
                </div>
              )}
            </DCard>
          </div>
        </>
      )}
    </div>
  );
}